import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import { AddCircleOutline as AddIcon, Edit as EditIcon, PersonAdd as PersonAddIcon, Close as CloseIcon } from '@mui/icons-material';
import { IPlanViewModel, IUserViewModel } from 'viewModels';
import { IPlanInputModel, IPlanUpdateInputModel } from 'inputModels';
import { BillingCycle, labelBillingCycle } from '../../../types/enums';
import { PlanPriceWarning } from '../../PlanPriceWarning';
import { Input } from '../../Inputs/Input';
import CurrencyInput from '../../CurrencyInput';
import { DropDownList } from '../../DropDownList';
import { UserSelector } from '../../UserSelector';

interface ModalPlanFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IPlanInputModel | IPlanUpdateInputModel) => Promise<void>;
  editingPlan: IPlanViewModel | null;
  loading: boolean;
}

export function ModalPlanForm({
  open,
  onClose,
  onSubmit,
  editingPlan,
  loading,
}: ModalPlanFormProps) {
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<IUserViewModel[]>([]);
  const [availableUsers, setAvailableUsers] = useState<IUserViewModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm<IPlanInputModel>({
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      disabled: false,
      billingCycle: BillingCycle.MONTHLY,
      userIds: [],
    },
  });

  // Dados para o dropdown de ciclo de cobrança
  const billingCycleOptions = Object.entries(labelBillingCycle).map(([key, value]) => ({
    id: key,
    value: value,
  }));

  // Carregar usuários disponíveis (mock data com paginação)
  const loadUsers = (page: number = 1) => {
    const usersPerPage = 6;
    const allMockUsers: IUserViewModel[] = [
      {
        id: '1',
        firstName: 'João',
        lastName: 'Silva',
        name: 'João Silva',
        email: 'joao@email.com',
        address: {
          street: 'Rua A',
          number: 123,
          city: 'São Paulo',
          uf: 'SP',
          postalCode: '01234-567',
          latitude: -23.5505,
          longitude: -46.6333
        }
      },
      {
        id: '2',
        firstName: 'Maria',
        lastName: 'Santos',
        name: 'Maria Santos',
        email: 'maria@email.com',
        address: {
          street: 'Rua B',
          number: 456,
          city: 'Rio de Janeiro',
          uf: 'RJ',
          postalCode: '20000-000',
          latitude: -22.9068,
          longitude: -43.1729
        }
      },
      {
        id: '3',
        firstName: 'Pedro',
        lastName: 'Costa',
        name: 'Pedro Costa',
        email: 'pedro@email.com',
        address: {
          street: 'Rua C',
          number: 789,
          city: 'Belo Horizonte',
          uf: 'MG',
          postalCode: '30000-000',
          latitude: -19.9167,
          longitude: -43.9345
        }
      },
      {
        id: '4',
        firstName: 'Ana',
        lastName: 'Oliveira',
        name: 'Ana Oliveira',
        email: 'ana@email.com',
        address: {
          street: 'Rua D',
          number: 321,
          city: 'Salvador',
          uf: 'BA',
          postalCode: '40000-000',
          latitude: -12.9714,
          longitude: -38.5014
        }
      },
      {
        id: '5',
        firstName: 'Carlos',
        lastName: 'Ferreira',
        name: 'Carlos Ferreira',
        email: 'carlos@email.com',
        address: {
          street: 'Rua E',
          number: 654,
          city: 'Brasília',
          uf: 'DF',
          postalCode: '70000-000',
          latitude: -15.7801,
          longitude: -47.9292
        }
      },
      {
        id: '6',
        firstName: 'Lucia',
        lastName: 'Almeida',
        name: 'Lucia Almeida',
        email: 'lucia@email.com',
        address: {
          street: 'Rua F',
          number: 987,
          city: 'Fortaleza',
          uf: 'CE',
          postalCode: '60000-000',
          latitude: -3.1190,
          longitude: -60.0217
        }
      },
      {
        id: '7',
        firstName: 'Roberto',
        lastName: 'Lima',
        name: 'Roberto Lima',
        email: 'roberto@email.com',
        address: {
          street: 'Rua G',
          number: 147,
          city: 'Recife',
          uf: 'PE',
          postalCode: '50000-000',
          latitude: -8.0476,
          longitude: -34.8770
        }
      },
      {
        id: '8',
        firstName: 'Fernanda',
        lastName: 'Rocha',
        name: 'Fernanda Rocha',
        email: 'fernanda@email.com',
        address: {
          street: 'Rua H',
          number: 258,
          city: 'Porto Alegre',
          uf: 'RS',
          postalCode: '90000-000',
          latitude: -30.0346,
          longitude: -51.2177
        }
      },
    ];

    const startIndex = (page - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const pageUsers = allMockUsers.slice(startIndex, endIndex);

    if (page === 1) {
      setAvailableUsers(pageUsers);
    } else {
      setAvailableUsers(prev => [...prev, ...pageUsers]);
    }

    setHasMoreUsers(endIndex < allMockUsers.length);
  };

  useEffect(() => {
    loadUsers(1);
  }, []);

  // Preencher valores quando estiver editando
  useEffect(() => {
    if (editingPlan) {
      reset({
        name: editingPlan.name,
        description: editingPlan.description,
        price: editingPlan.price,
        disabled: editingPlan.disabled,
        billingCycle: editingPlan.billingCycle,
        userIds: editingPlan.users?.map((user) => user.id),
      });
      setSelectedUsers((editingPlan.users || []).map(user => ({
        ...user,
        firstName: user.name.split(' ')[0] || '',
        lastName: user.name.split(' ').slice(1).join(' ') || '',
      })));
    } else {
      reset({
        name: '',
        description: '',
        price: 0,
        disabled: false,
        billingCycle: BillingCycle.MONTHLY,
        userIds: [],
      });
      setSelectedUsers([]);
    }
  }, [editingPlan, reset]);

  const handleFormSubmit = async (data: IPlanInputModel) => {
    try {
      // Incluir usuários selecionados nos dados
      const dataWithUsers = {
        ...data,
        userIds: selectedUsers.map(user => user.id),
      };
      await onSubmit(dataWithUsers);
      reset();
      setSelectedUsers([]);
      onClose();
    } catch (error) {
      // Error is handled by the parent component
    }
  };

  const handleClose = () => {
    reset();
    setSelectedUsers([]);
    onClose();
  };

  const handleUserSelection = (users: IUserViewModel[]) => {
    setSelectedUsers(users);
    setValue('userIds', users.map(user => user.id));
  };

  const handleRemoveUser = (userId: string) => {
    const updatedUsers = selectedUsers.filter(user => user.id !== userId);
    setSelectedUsers(updatedUsers);
    setValue('userIds', updatedUsers.map(user => user.id));
  };

  const handleLoadMoreUsers = async () => {
    setLoadingMoreUsers(true);
    // Simular delay de API
    setTimeout(() => {
      setCurrentPage(prev => {
        const nextPage = prev + 1;
        loadUsers(nextPage);
        return nextPage;
      });
      setLoadingMoreUsers(false);
    }, 1000);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {editingPlan ? (
            <EditIcon fontSize="small" />
          ) : (
            <AddIcon fontSize="small" />
          )}
          <Typography variant="h6" component="span">
            {editingPlan ? 'Editar Plano' : 'Novo Plano'}
          </Typography>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <PlanPriceWarning
            isEditing={!!editingPlan}
            originalPrice={editingPlan?.price}
            newPrice={watch('price')}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Input
                control={control}
                name="name"
                label="Nome do Plano"
                placeholder="Digite o nome do plano"
                disabled={loading}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CurrencyInput
                label="Preço"
                value={watch('price') || 0}
                onChange={(value) => setValue('price', value)}
                disabled={loading}
                variant="outlined"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                control={control}
                name="description"
                label="Descrição"
                placeholder="Descreva o plano"
                multiline
                rows={3}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropDownList
                itens={billingCycleOptions}
                placeholderTitle="Selecione o ciclo de cobrança"
                typeReturn="id"
                onChange={(value) => setValue('billingCycle', Number(value))}
                value={watch('billingCycle')?.toString()}
                disabled={loading}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="disabled"
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!value}
                        onChange={(e) => onChange(!e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Plano Ativo"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {editingPlan ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </form>

      {/* Modal de Seleção de Usuários */}
      <UserSelector
        open={showUserSelector}
        onClose={() => setShowUserSelector(false)}
        onConfirm={handleUserSelection}
        selectedUsers={selectedUsers}
        availableUsers={availableUsers}
        loading={loading}
        title="Selecionar Usuários do Plano"
        hasMoreUsers={hasMoreUsers}
        onLoadMore={handleLoadMoreUsers}
        loadingMore={loadingMoreUsers}
      />
    </Dialog>
  );
}
